import { useRef } from 'react';

import { isAppAllowed } from '@cartwhl/common/appletPolicy';
import type { AppEntitlement } from '@cartwhl/common/types';
import { getAppletsUrl, getStudioUrl } from '@cartwhl/common/utils';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Text from '@cartwhl/ui/base/TextNew';
import GridIcon from '@cartwhl/ui/icons/GridIcon';
import Button from '@cartwhl/ui/input/Button';
import { HEADER_ICON_BUTTON_CLASSNAME } from '@cartwhl/ui/layout/StudioShellHeader';

export const APPS_MENU_CDN_BASE =
  'https://resources.getcartwheel.com/share/demo-videos';

/** Same clips as the former profile-dropdown Apps submenu and homepage product cards. */
export const APPS_MENU_PREVIEW_VIDEO = {
  comic: `${APPS_MENU_CDN_BASE}/01_home_page/03_platform_capabilities_section/02_comic_update.webm`,
  swing: `${APPS_MENU_CDN_BASE}/v2_homepage_platform_capabilities_03_swing.webm`,
  motionEditor: `${APPS_MENU_CDN_BASE}/06_motionEditor_menu_output.webm`,
  cartographer: `${APPS_MENU_CDN_BASE}/cartographer/00_home_run_output.webm`,
  characterGenerator:
    'https://resources.getcartwheel.com/share/demo-videos/v2_product_character_gen_hero_dashboard.webm',
  mascot: `${APPS_MENU_CDN_BASE}/mascot/00_home_dribble.webm`,
  canvas: `${APPS_MENU_CDN_BASE}/v2_homepage_05_try_it_in_canvas.webm`,
} as const;

export const MOTION_EDITOR_APPS_MENU_VIDEO =
  APPS_MENU_PREVIEW_VIDEO.motionEditor;

export type AppsMenuItem = {
  id: AppEntitlement;
  external?: boolean;
  previewStartSeconds?: number;
  subtitle: string;
  title: string;
  to: string;
  videoSrc: string;
};

/** Same apps and order as the applets dashboard; preview media matches the old profile Apps submenu. */
export const APPS_MENU_ITEMS: AppsMenuItem[] = [
  {
    id: 'comic',
    subtitle: 'AI video mocap',
    title: 'Performance Capture',
    to: `${getAppletsUrl()}/comic`,
    videoSrc: APPS_MENU_PREVIEW_VIDEO.comic,
  },
  {
    id: 'generation',
    subtitle: 'Text-to-motion',
    title: 'Performance Generation',
    to: `${getAppletsUrl()}/generation`,
    videoSrc: APPS_MENU_PREVIEW_VIDEO.swing,
    previewStartSeconds: 1.5,
  },
  {
    id: 'motion-editor',
    subtitle: 'Generative editing and reshaping',
    title: 'Performance Editor',
    to: `${getAppletsUrl()}/motion-editor`,
    videoSrc: APPS_MENU_PREVIEW_VIDEO.motionEditor,
  },
  {
    id: 'cartographer',
    subtitle: 'Universal motion retargeting',
    title: 'Cartographer',
    to: `${getAppletsUrl()}/cartographer`,
    videoSrc: APPS_MENU_PREVIEW_VIDEO.cartographer,
  },
  {
    id: 'canvas',
    external: true,
    subtitle: 'Comprehensive motion suite',
    title: 'Canvas',
    to: getStudioUrl(),
    videoSrc: APPS_MENU_PREVIEW_VIDEO.canvas,
    previewStartSeconds: 3,
  },
  {
    id: 'character',
    subtitle: 'Rigged 3D characters from prompt or art',
    title: 'Character Generator',
    to: `${getAppletsUrl()}/character`,
    videoSrc: APPS_MENU_PREVIEW_VIDEO.characterGenerator,
  },
];

const APPS_MENU_DROPDOWN_Z_INDEX = 60;

const appsMenuPreviewOuterClassName =
  'border-primary shrink-0 rounded-md border border-solid';
const appsMenuPreviewInnerClassName =
  'bg-gray-15 h-[72px] w-[52px] overflow-hidden rounded-[5px]';

export default function AppsMenu({
  allowedApps,
  appletHeader = false,
}: {
  allowedApps?: readonly AppEntitlement[];
  appletHeader?: boolean;
}) {
  const allowedItems = APPS_MENU_ITEMS.filter((item) =>
    isAppAllowed(item.id, allowedApps),
  );
  if (allowedItems.length === 0) return null;

  return (
    <Dropdown
      side="bottom"
      align="end"
      zIndex={APPS_MENU_DROPDOWN_Z_INDEX}
      trigger={
        appletHeader ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={<GridIcon />}
            iconSize={20}
            className="p-1"
            aria-label="Apps"
          />
        ) : (
          <button
            type="button"
            className={HEADER_ICON_BUTTON_CLASSNAME}
            aria-label="Apps"
          >
            <GridIcon className="h-7 w-7" />
          </button>
        )
      }
    >
      <div className="flex w-[min(100vw-2rem,560px)] flex-col gap-1 p-1">
        <Text token="14/medium" className="text-tertiary px-2 pt-1">
          Cartwheel apps
        </Text>
        <div className="grid grid-cols-2 gap-1">
          {allowedItems.map((item) => (
            <AppsMenuGridItem key={item.to} {...item} />
          ))}
        </div>
      </div>
    </Dropdown>
  );
}

function AppsMenuGridItem({
  external,
  previewStartSeconds,
  subtitle,
  title,
  to,
  videoSrc,
}: AppsMenuItem) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const seekToPreviewStart = (video: HTMLVideoElement) => {
    if (previewStartSeconds === undefined) {
      return;
    }

    if (Math.abs(video.currentTime - previewStartSeconds) > 0.05) {
      video.currentTime = previewStartSeconds;
    }
  };

  const playPreview = () => {
    const video = videoRef.current;
    if (!video) return;

    seekToPreviewStart(video);
    void video.play().catch(() => undefined);
  };

  const stopPreview = () => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    seekToPreviewStart(video);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || previewStartSeconds === undefined || video.paused) {
      return;
    }

    if (video.currentTime < previewStartSeconds - 0.05) {
      video.currentTime = previewStartSeconds;
    }
  };

  return (
    <a
      href={to}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
      className="hover:bg-gray-15 dark:hover:bg-gray-90 flex min-h-[88px] items-center gap-3 rounded-lg p-2 transition-colors"
      onBlur={stopPreview}
      onFocus={playPreview}
      onMouseEnter={playPreview}
      onMouseLeave={stopPreview}
    >
      <div className={appsMenuPreviewOuterClassName}>
        <div className={appsMenuPreviewInnerClassName}>
          <video
            ref={videoRef}
            src={videoSrc}
            className="block h-full w-full object-cover object-center"
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden
            onLoadedMetadata={(event) => {
              seekToPreviewStart(event.currentTarget);
            }}
            onTimeUpdate={handleTimeUpdate}
          />
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Text token="14/medium" className="text-primary truncate">
          {title}
        </Text>
        <Text token="14/medium" className="text-tertiary line-clamp-2">
          {subtitle}
        </Text>
      </div>
    </a>
  );
}
